<template>
  <b-modal
    ref="bulk-edit-modal"
    id="bulk-edit-modal"
    class="bulk-edit-modal"
    hide-header
    centered
    no-close-on-backdrop
    no-close-on-esc
  >
    <div
      class="d-flex justify-content-between top-modal"
      style="margin-top: 10px;"
    >
      <div>
        <h4 class="font-medium text-primary text-capitalize">
          Bulk Edit Ad Group {{ typeEdit }}
        </h4>
        <p class="mb-0 text-placeholder">
          Set a new {{ typeEdit }} for selected ad groups in selected campaigns.
        </p>
      </div>
      <div>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hideModal"
        />
      </div>
    </div>
    <div class="bulk-edit-content my-2">
      <b-form>
        <b-form-group
          class="group-input"
          label-cols-md="3"
          content-cols-md="9"
          label="Campaign"
        >
          <b-input-group
            class="input-group-merge"
          >
            <v-select
              v-model="campaignSelected"
              style="width: 100%"
              class="input-height bulk-edit-input"
              label="name"
              :clearable="false"
              :options="campaignOptions"
            >
              <template #open-indicator="{ attributes }">
                <span v-bind="attributes">
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="20"
                  />
                </span>
              </template>
              <template #selected-option="{ name }">
                <div
                  v-b-tooltip.hover.top.v-light.html
                  class="d-flex flex-column content-ad-account w-100"
                  :title="name ? name : '--'"
                >
                  <span
                    class="wrap-content"
                    style="margin-right: 4px"
                  >{{ name }}</span>
                </div>
              </template>
              <template #option="{ name }">
                <div class="d-flex justify-content-between">
                  <span>{{ name }}</span>
                </div>
              </template>
            </v-select>
          </b-input-group>
        </b-form-group>

        <b-form-group
          class="group-input"
          label-cols-md="3"
          content-cols-md="9"
          label="Ad Group"
        >
          <b-input-group
            class="input-group-merge"
          >
            <v-select
              v-model="adGroupSelected"
              style="width: 100%"
              class="input-height bulk-edit-input"
              :clearable="false"
              :disabled="campaignSelected.value === 'all'"
              :options="adGroupOptions"
            >
              <template #open-indicator="{ attributes }">
                <span v-bind="attributes">
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="20"
                  />
                </span>
              </template>
              <template #selected-option="{ name }">
                <div
                  v-b-tooltip.hover.top.v-light.html
                  class="d-flex flex-column content-ad-account w-100"
                  :title="name ? name : '--'"
                >
                  <span
                    class="wrap-content"
                    style="margin-right: 4px"
                  >{{ name }}</span>
                </div>
              </template>
              <template #option="{ name }">
                <div class="d-flex justify-content-between">
                  <span>{{ name }}</span>
                </div>
              </template>
            </v-select>
          </b-input-group>
        </b-form-group>

        <b-form-group v-if="typeEdit === 'schedule time'">
          <b-form-radio
            v-model="scheduleType"
            :disabled="isCampaignBudgetTotal || isAdgroupBudgetTotal"
            name="some-radios3"
            value="SCHEDULE_FROM_NOW"
            class="custom-control"
          >
            Set start time to run ad group continuously

            <div v-if="scheduleType === 'SCHEDULE_FROM_NOW'">
              <flat-pickr
                v-model="startDate"
                :class="['form-control', 'mt-1', { 'is-invalid': messageErrorDate }]"
                :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', allowInput: true, allowInvalidPreload: true, minDate: todayStartOfDay }"
                @input="changeStartDate"
              />
              <small
                v-if="messageErrorDate"
                class="text-danger d-block mt-25"
              >{{ messageErrorDate }}</small>
              <small class="text-placeholder mt-25">
                Ad delivery is based on your registered account timezone ({{ etcToUTC(adAccountSelected.timezone) }}).
              </small>
            </div>
          </b-form-radio>
          <b-form-radio
            v-model="scheduleType"
            name="some-radio9"
            value="SCHEDULE_START_END"
            class="custom-control mt-2"
          >
            Set start time and end time to run ad group

            <div v-if="scheduleType === 'SCHEDULE_START_END'">
              <flat-pickr
                v-model="rangeDate"
                :class="['form-control', 'mt-1', { 'is-invalid': messageErrorRangeDate }]"
                :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', mode: 'range', allowInput: true, allowInvalidPreload: true, minDate: todayStartOfDay }"
                @input="changeRangeDate"
              />
              <small
                v-if="messageErrorRangeDate"
                class="text-danger d-block mt-25"
              >{{ messageErrorRangeDate }}</small>
              <small class="text-placeholder mt-25">
                Ad delivery is based on your registered account timezone ({{ etcToUTC(adAccountSelected.timezone) }}).
              </small>
            </div>
          </b-form-radio>
        </b-form-group>
      </b-form>
    </div>

    <!-- Action Buttons -->
    <template #modal-footer>
      <div class="d-flex justify-content-end">
        <btn-loading
          class="mr-1 border-0"
          variant-convert="btn-back"
          pill
          @click="hideModal"
        >
          Cancel
        </btn-loading>
        <btn-loading
          class="ml-1"
          variant-convert="btn-submit"
          type="submit"
          pill
          @click="confirmEdit"
        >
          Apply
        </btn-loading>
      </div>
    </template>
  </b-modal>
</template>
<script>
import { createNamespacedHelpers } from 'vuex'
import {
  BModal,
  BFormRadio,
  BInputGroup,
  BFormGroup,
  BForm, VBTooltip,
} from 'bootstrap-vue'
import BtnLoading from '@/layouts/components/BtnLoading.vue'
import flatPickr from 'vue-flatpickr-component'
import dayjs from 'dayjs'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import campaignMixin from '@/mixins/campaignMixin'

const { mapActions, mapGetters } = createNamespacedHelpers('campaign')

export default {
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  components: {
    BFormGroup,
    BInputGroup,
    BModal,
    BtnLoading,
    vSelect,
    BFormRadio,
    flatPickr,
    BForm,
  },
  mixins: [campaignMixin],
  props: {
    campaigns: {
      type: Array,
      default: () => [],
      required: true,
    },

    adAccountSelected: {
      type: Object,
      default: () => ({}),
      required: true,
    },

    isCampaignBudgetTotal: {
      type: Boolean,
      default: false,
    },

    isAdgroupBudgetTotal: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      scheduleType: 'SCHEDULE_FROM_NOW', // or 'SCHEDULE_START_END'

      startDate: null,
      rangeDate: null,
      now: new Date(),

      messageErrorDate: '',
      messageErrorRangeDate: '',

      roasBid: '',

      typeEdit: '',

      campaignSelected: '',
      campaignOptions: [],
      adGroupSelected: '',
      adGroupOptions: [],

      updatedCampaigns: [],
    }
  },

  computed: {
    ...mapGetters(['listIdentity', 'listVideoTikTok', 'status', 'message', 'loading']),
    todayStartOfDay() {
      return dayjs().subtract(1, 'day').tz(this.adAccountSelected.timezone).toDate()
    },
  },

  watch: {
    campaignSelected: {
      handler(campaign) {
        const allOption = { name: 'All Ad group', value: 'all' }
        if (this.campaigns[campaign.value]) {
          this.adGroupOptions = [allOption, ...this.campaigns[campaign.value].adgroupList.map((adgroup, index) => ({
            name: adgroup.adgroupName,
            value: index,
          }))]
        } else {
          this.adGroupOptions = [allOption]
        }
        this.adGroupSelected = allOption
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    ...mapActions(['getListIdentity', 'getListVideoTikTok']),
    initData() {
      if (this.isCampaignBudgetTotal || this.isAdgroupBudgetTotal) {
        this.scheduleType = 'SCHEDULE_START_END'
      }
      this.updatedCampaigns = JSON.parse(JSON.stringify(this.campaigns))

      const allOption = { name: 'All Campaign', value: 'all' }
      this.campaignOptions = [allOption, ...this.campaigns.map((campaign, index) => ({
        name: campaign.campaignName,
        value: index,
      }))]

      this.campaignSelected = allOption

      this.startDate = dayjs().add(5, 'minute').tz(this.adAccountSelected.timezone).format('YYYY-MM-DD HH:mm')
      this.rangeDate = `${dayjs().add(5, 'minute').tz(this.adAccountSelected.timezone).format('YYYY-MM-DD HH:mm')} to ${dayjs().add(2, 'week').add(5, 'minute').tz(this.adAccountSelected.timezone)
        .format('YYYY-MM-DD HH:mm')}`
    },

    hideModal() {
      this.$refs['bulk-edit-modal'].hide()
    },

    async showModal(type) {
      this.typeEdit = type

      this.initData()
      this.$refs['bulk-edit-modal'].show()
    },

    changeStartDate() {
      const tz = this.adAccountSelected.timezone
      const start = dayjs.tz(this.startDate, tz)
      const nowTz = dayjs().tz(tz)
      if (start.isBefore(nowTz, 'minute')) {
        this.messageErrorDate = "Start time can't be earlier than the current time"
      } else {
        this.messageErrorDate = ''
      }
    },

    changeRangeDate() {
      const tz = this.adAccountSelected.timezone
      const [rangeStart, rangeEnd] = this.rangeDate.split('to').map(s => s.trim())
      const start = dayjs.tz(rangeStart, tz)
      const end = dayjs.tz(rangeEnd, tz)
      const nowTz = dayjs().tz(tz)

      if (start.isBefore(nowTz, 'minute')) {
        this.messageErrorRangeDate = "Start time can't be earlier than the current time"
      } else if (!start.isBefore(end, 'minute')) {
        this.messageErrorRangeDate = 'Start date must be earlier than end date.'
      } else {
        this.messageErrorRangeDate = ''
      }
    },

    editScheduleTime() {
      let scheduleStartTime = dayjs().format('YYYY-MM-DD HH:mm:ss')
      let scheduleEndTime = dayjs().format('YYYY-MM-DD HH:mm:ss')
      if (this.scheduleType === 'SCHEDULE_FROM_NOW') {
        scheduleStartTime = dayjs(this.startDate).format('YYYY-MM-DD HH:mm:ss')
        scheduleEndTime = dayjs(this.startDate).add(1, 'year').format('YYYY-MM-DD HH:mm:ss')
      } else if (this.scheduleType === 'SCHEDULE_START_END') {
        const start = this.rangeDate?.split('to')[0].trim()
        const end = this.rangeDate?.split('to')[1].trim()
        scheduleStartTime = dayjs(start).format('YYYY-MM-DD HH:mm:ss')
        scheduleEndTime = dayjs(end).format('YYYY-MM-DD HH:mm:ss')
      }

      if (this.campaignSelected.value !== 'all') { // specific campaign
        if (this.adGroupSelected.value !== 'all') { // specific adgroup
          const adgroup = this.updatedCampaigns[this.campaignSelected.value].adgroupList[this.adGroupSelected.value]
          adgroup.scheduleType = this.scheduleType
          adgroup.scheduleStartTime = scheduleStartTime
          adgroup.scheduleEndTime = scheduleEndTime
        } else {
          // all adgroups in specific campaign
          this.updatedCampaigns[this.campaignSelected.value].adgroupList = this.updatedCampaigns[this.campaignSelected.value].adgroupList.map(agGroup => ({
            ...agGroup,
            scheduleStartTime,
            scheduleEndTime,
            scheduleType: this.scheduleType,
          }))
        }
      } else {
        // all campaigns and adgroups
        this.updatedCampaigns.forEach((campaign, campaignIndex) => {
          this.updatedCampaigns[campaignIndex].adgroupList = campaign.adgroupList.map(adgroup => ({
            ...adgroup,
            scheduleStartTime,
            scheduleEndTime,
            scheduleType: this.scheduleType,
          }))
        })
      }
    },

    confirmEdit() {
      if (this.typeEdit === 'schedule time') {
        this.editScheduleTime()
      }

      this.$emit('update-campaigns', this.updatedCampaigns)
      this.hideModal()
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-select.scss';
#bulk-edit-modal {
  .modal-content {
    .modal-body {
      height: 100%;
    }

    .form-control:focus, .input-group:focus-within .form-control {
      border-color: #e0e0e5 !important;
    }

  }

  .bulk-edit-input {
    .vs__selected-options {
      width: 90%;
      .vs__selected {
        width: 90%;
      }
    }
  }
}
</style>
<style lang="scss" scoped>
.wrap-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
